import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, } from '@angular/material/snack-bar';
import { Observable, catchError, tap } from 'rxjs';
import { SnackbarComponent } from './snackbar.component';

export type SnackbarType = 'warn' | 'error' | 'info' | 'success'

export interface ISnackbarData<TTranslationKey extends string = string> {

  type: SnackbarType,
  title?: TTranslationKey,
  message: TTranslationKey,
  action?: TTranslationKey
  hideClose?: boolean,
  linkUrl?: string,
  offsetTop?: boolean
}

export interface IHandleErrorParams<TTranslationKey extends string = string> extends Omit<ISnackbarData<TTranslationKey>, 'type' | 'message'> {

  message?: TTranslationKey
  getErrorMessage?: (error: any) => TTranslationKey

  type: Extract<SnackbarType, 'error' | 'warning'>
}

export interface IHandleAsync<TTranslationKey extends string = string> {

  errorMessage?: TTranslationKey
  getErrorMessage?: (error: any) => TTranslationKey

  successMessage?: TTranslationKey,
  getSuccessMessage?: (result: any) => TTranslationKey
}


@Injectable({
  providedIn: 'root'
})
export class SnackbarService<TTranslationKey extends string = string> {

  private _snackbarRef: MatSnackBarRef<SnackbarComponent>;

  readonly handleError = (params: IHandleErrorParams<TTranslationKey>) => <T extends any>(obs: Observable<T>) =>
    obs.pipe(
      catchError((error) => {

        this.open({
          ...params,

          message: params.message || params.getErrorMessage?.(error),
        })

        throw error
      })
    )


  readonly handleAsync = (params: IHandleAsync<TTranslationKey>) => <T extends any>(obs: Observable<T>) =>
    obs.pipe(
      this.handleError({
        type: 'error',
        getErrorMessage: params.getErrorMessage,
        message: params.errorMessage,
      }),
      tap((result) => {

        this.open({
          type: 'success',
          message: params.successMessage || params.getSuccessMessage(result),

        },
          {
            duration: 1.5 * 1000
          })
      }),
    )

  constructor(
    private readonly _snackbar: MatSnackBar,
  ) { }


  open(snackbarData: ISnackbarData<TTranslationKey>, options?: Omit<MatSnackBarConfig, 'data'>) {

    const optionsWithDefaults: MatSnackBarConfig = {
      direction: options?.direction || 'ltr',

      announcementMessage: options?.announcementMessage || '',
      duration: options?.duration || 8 * 1000,
      horizontalPosition: 'center',
      politeness: options?.politeness || 'polite',
      verticalPosition: options?.verticalPosition || 'bottom',
      ...options,
      panelClass: 'snackbar-panel',
      data: { ...snackbarData, position: options?.verticalPosition || 'bottom' },
    }

    this._snackbarRef = this._snackbar.openFromComponent(
      SnackbarComponent,
      optionsWithDefaults
    )

    return this._snackbarRef.afterDismissed()
  }


}
